import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServicesForm = ({ formData, onUpdate, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});
  const [showTooltip, setShowTooltip] = useState(null);

  // Enhanced cultural specializations - removed duplicates, added new groups
  const culturalSpecializations = [
    { id: 'punjabi-sikh', label: 'Punjabi/Sikh', description: 'Sikh traditions & Anand Karaj ceremonies', icon: 'Heart' },
    { id: 'gujarati', label: 'Gujarati', description: 'Gujarati wedding customs', icon: 'Heart' },
    { id: 'south-indian', label: 'South Indian', description: 'Tamil/Telugu/Kannada traditions', icon: 'Heart' },
    { id: 'bengali', label: 'Bengali', description: 'Bengali rituals & ceremonies', icon: 'Heart' },
    { id: 'pakistani', label: 'Pakistani', description: 'Nikah, Mehndi, Barat, Walima ceremonies', icon: 'Heart' },
    { id: 'bangladeshi', label: 'Bangladeshi', description: 'Bangladeshi wedding traditions', icon: 'Heart' },
    { id: 'sri-lankan-tamil', label: 'Sri Lankan / Tamil Hindu', description: 'Sri Lankan Tamil customs', icon: 'Heart' },
    { id: 'nepali-bhutanese', label: 'Nepali / Bhutanese', description: 'Himalayan wedding traditions', icon: 'Heart' },
    { id: 'marathi', label: 'Marathi', description: 'Maharashtrian traditions', icon: 'Heart' },
    { id: 'hindu-general', label: 'Hindu', description: 'General Hindu wedding ceremonies', icon: 'Heart' },
    { id: 'muslim-general', label: 'Muslim', description: 'Islamic wedding traditions', icon: 'Heart' },
    { id: 'interfaith', label: 'Interfaith', description: 'Mixed cultural traditions', icon: 'Heart' },
    { id: 'fusion', label: 'American Fusion', description: 'Modern & traditional blend', icon: 'Heart' }
  ];

  // Enhanced event types - grouped by tradition with new Sikh events
  const eventTypesByTradition = {
    'Hindu': [
      { id: 'engagement', label: 'Engagement', description: 'Ring ceremony & celebration' },
      { id: 'mehndi', label: 'Mehndi/Henna', description: 'Henna application ceremony' },
      { id: 'sangeet', label: 'Sangeet', description: 'Music & dance celebration' },
      { id: 'haldi', label: 'Haldi/Pithi', description: 'Turmeric ceremony' },
      { id: 'mehendi', label: 'Mehendi', description: 'Bridal henna ceremony' },
      { id: 'wedding-hindu', label: 'Hindu Wedding Ceremony', description: 'Traditional Hindu wedding ritual' },
      { id: 'reception', label: 'Reception', description: 'Wedding reception party' }
    ],
    'Muslim': [
      { id: 'nikah', label: 'Nikkah', description: 'Islamic wedding ceremony' },
      { id: 'mehndi-muslim', label: 'Mehndi', description: 'Pre-wedding henna ceremony' },
      { id: 'barat', label: 'Barat', description: 'Groom\'s procession ceremony' },
      { id: 'walima', label: 'Walima', description: 'Reception hosted by groom\'s family' },
      { id: 'reception-muslim', label: 'Reception', description: 'Wedding reception celebration' }
    ],
    'Sikh': [
      { id: 'jaggo', label: 'Jaggo', description: 'Pre-wedding celebration with singing' },
      { id: 'maiyaan', label: 'Maiyaan', description: 'Turmeric and oil ceremony' },
      { id: 'anand-karaj', label: 'Anand Karaj', description: 'Sikh wedding ceremony' },
      { id: 'reception-sikh', label: 'Reception', description: 'Post-wedding celebration' }
    ],
    'General': [
      { id: 'destination', label: 'Destination Wedding', description: 'Out-of-town weddings' },
      { id: 'engagement-general', label: 'Engagement Party', description: 'Engagement celebration' },
      { id: 'anniversary', label: 'Anniversary Celebration', description: 'Wedding anniversary events' }
    ]
  };

  // Enhanced languages - added Pashto, Sindhi, Sinhala
  const languagesSpoken = [
    { id: 'english', label: 'English' },
    { id: 'hindi', label: 'Hindi' },
    { id: 'punjabi', label: 'Punjabi' },
    { id: 'gujarati', label: 'Gujarati' },
    { id: 'urdu', label: 'Urdu' },
    { id: 'tamil', label: 'Tamil' },
    { id: 'telugu', label: 'Telugu' },
    { id: 'bengali', label: 'Bengali' },
    { id: 'marathi', label: 'Marathi' },
    { id: 'kannada', label: 'Kannada' },
    { id: 'pashto', label: 'Pashto' },
    { id: 'sindhi', label: 'Sindhi' },
    { id: 'sinhala', label: 'Sinhala' }
  ];

  // Enhanced pricing models with tooltips
  const pricingModels = [
    { 
      id: 'package', 
      label: 'Package Pricing', 
      description: 'Fixed packages with set services',
      tooltip: 'Best for vendors offering comprehensive service bundles'
    },
    { 
      id: 'hourly', 
      label: 'Hourly Rate', 
      description: 'Charge by the hour',
      tooltip: 'Ideal for services charged by time (photographers, musicians)'
    },
    { 
      id: 'custom', 
      label: 'Custom Quotes', 
      description: 'Personalized pricing per event',
      tooltip: 'Perfect for complex services requiring individual assessment'
    },
    { 
      id: 'tiered', 
      label: 'Tiered Pricing', 
      description: 'Multiple service levels',
      tooltip: 'Great for offering basic, standard, and premium options'
    }
  ];

  const handleArrayToggle = (field, value) => {
    const currentArray = formData[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    onUpdate({ [field]: newArray });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.culturalSpecializations?.length) {
      newErrors.culturalSpecializations = 'Please select at least one cultural specialization';
    }
    if (!formData.eventTypes?.length) {
      newErrors.eventTypes = 'Please select at least one event type you serve';
    }
    if (!formData.languagesSpoken?.length) {
      newErrors.languagesSpoken = 'Please select at least one language you speak';
    }
    if (!formData.pricingModel) {
      newErrors.pricingModel = 'Please select your pricing model';
    }
    if (!formData.serviceDescription?.trim()) {
      newErrors.serviceDescription = 'Please provide a service description';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  // Get all event types as flat array
  const getAllEventTypes = () => {
    return Object.values(eventTypesByTradition).flat();
  };

  return (
    <div className="space-y-8">
      {/* Cultural Specializations */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Cultural Specializations
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Which cultural traditions do you specialize in? Select all that apply. Our platform serves diverse South Asian communities.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {culturalSpecializations.map((culture) => (
            <button
              key={culture.id}
              onClick={() => handleArrayToggle('culturalSpecializations', culture.id)}
              className={`p-4 rounded-xl border-2 text-left transition-smooth hover:shadow-soft ${
                formData.culturalSpecializations?.includes(culture.id)
                  ? 'border-primary bg-primary-50 shadow-soft'
                  : 'border-border hover:border-primary-200 bg-white'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                  formData.culturalSpecializations?.includes(culture.id)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface-100 text-text-secondary'
                }`}>
                  <Icon name={culture.icon} size={16} color="currentColor" />
                </div>
                <div className="flex-1">
                  <h4 className={`font-medium text-sm ${
                    formData.culturalSpecializations?.includes(culture.id) ? 'text-primary' : 'text-text-primary'
                  }`}>
                    {culture.label}
                  </h4>
                  <p className="text-xs text-text-muted font-caption mt-1">
                    {culture.description}
                  </p>
                </div>
                {formData.culturalSpecializations?.includes(culture.id) && (
                  <Icon name="Check" size={16} color="#8B4513" />
                )}
              </div>
            </button>
          ))}
        </div>
        
        {errors.culturalSpecializations && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.culturalSpecializations}</span>
          </p>
        )}
      </div>

      {/* Event Types - Grouped by Tradition */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Event Types You Serve
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          What types of wedding events do you provide services for? Events are organized by cultural tradition.
        </p>
        
        <div className="space-y-6">
          {Object.entries(eventTypesByTradition).map(([tradition, events]) => (
            <div key={tradition} className="space-y-3">
              <h4 className="font-heading font-semibold text-lg text-text-primary flex items-center space-x-2">
                <Icon name="Star" size={16} color="#8B4513" />
                <span>{tradition} Events</span>
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {events.map((event) => (
                  <label
                    key={event.id}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-smooth hover:shadow-soft ${
                      formData.eventTypes?.includes(event.id)
                        ? 'border-primary bg-primary-50 shadow-soft'
                        : 'border-border hover:border-primary-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.eventTypes?.includes(event.id) || false}
                        onChange={() => handleArrayToggle('eventTypes', event.id)}
                        className="mt-1 w-4 h-4 text-primary border-border-dark rounded focus:ring-primary focus:ring-2"
                      />
                      <div className="flex-1">
                        <h4 className={`font-medium text-sm ${
                          formData.eventTypes?.includes(event.id) ? 'text-primary' : 'text-text-primary'
                        }`}>
                          {event.label}
                        </h4>
                        <p className="text-xs text-text-muted font-caption mt-1">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {errors.eventTypes && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.eventTypes}</span>
          </p>
        )}
      </div>

      {/* Languages Spoken */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Languages You Speak
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          What languages can you communicate in with your clients? This helps couples feel comfortable working with you.
        </p>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {languagesSpoken.map((language) => (
            <label
              key={language.id}
              className={`p-3 rounded-lg border-2 cursor-pointer text-center transition-smooth hover:shadow-soft ${
                formData.languagesSpoken?.includes(language.id)
                  ? 'border-primary bg-primary-50 text-primary' :'border-border hover:border-primary-200 bg-white text-text-primary'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.languagesSpoken?.includes(language.id) || false}
                onChange={() => handleArrayToggle('languagesSpoken', language.id)}
                className="sr-only"
              />
              <span className="font-medium text-sm">{language.label}</span>
            </label>
          ))}
        </div>
        
        {errors.languagesSpoken && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.languagesSpoken}</span>
          </p>
        )}
      </div>

      {/* Service Description */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Service Description
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Describe your services and what makes you unique
        </p>
        
        <textarea
          placeholder={`Tell couples about your services, experience, and what makes you special...\n\nExample: "We specialize in traditional Punjabi weddings with over 10 years of experience. Our team understands the cultural significance of each ceremony and ensures every moment is captured beautifully."`}
          value={formData.serviceDescription || ''}
          onChange={(e) => handleInputChange('serviceDescription', e.target.value)}
          rows={6}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none ${
            errors.serviceDescription ? 'border-error' : 'border-border'
          }`}
        />
        
        {errors.serviceDescription && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.serviceDescription}</span>
          </p>
        )}
      </div>

      {/* Pricing Model with Tooltips */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Pricing Model
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          How do you structure your pricing? Hover over options for guidance.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pricingModels.map((model) => (
            <div
              key={model.id}
              className="relative"
              onMouseEnter={() => setShowTooltip(model.id)}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <button
                onClick={() => handleInputChange('pricingModel', model.id)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-smooth hover:shadow-soft ${
                  formData.pricingModel === model.id
                    ? 'border-primary bg-primary-50 shadow-soft'
                    : 'border-border hover:border-primary-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className={`font-medium text-sm ${
                      formData.pricingModel === model.id ? 'text-primary' : 'text-text-primary'
                    }`}>
                      {model.label}
                    </h4>
                    <p className="text-xs text-text-muted font-caption mt-1">
                      {model.description}
                    </p>
                  </div>
                  <Icon name="HelpCircle" size={16} color="#6B5B4F" />
                </div>
              </button>
              
              {/* Tooltip */}
              {showTooltip === model.id && (
                <div className="absolute z-10 top-full left-0 right-0 mt-2 p-3 bg-text-primary text-white text-xs rounded-lg shadow-strong">
                  {model.tooltip}
                  <div className="absolute -top-1 left-4 w-2 h-2 bg-text-primary transform rotate-45"></div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {errors.pricingModel && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.pricingModel}</span>
          </p>
        )}
      </div>

      {/* Service Area */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Service Area
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          How far are you willing to travel for events?
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Travel Radius (miles)
            </label>
            <select
              value={formData.travelRadius || '25'}
              onChange={(e) => handleInputChange('travelRadius', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="10">Within 10 miles</option>
              <option value="25">Within 25 miles</option>
              <option value="50">Within 50 miles</option>
              <option value="100">Within 100 miles</option>
              <option value="unlimited">Unlimited (destination weddings)</option>
            </select>
          </div>

          <div className="space-y-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.destinationWeddings || false}
                onChange={(e) => handleInputChange('destinationWeddings', e.target.checked)}
                className="w-4 h-4 text-primary border-border-dark rounded focus:ring-primary focus:ring-2"
              />
              <span className="text-sm font-medium text-text-primary">
                Available for destination weddings
              </span>
            </label>
            <p className="text-xs text-text-muted font-caption ml-6">
              Check if you're willing to travel anywhere for the right event
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconSize={18}
        >
          Back to Basic Info
        </Button>
        
        <Button
          variant="primary"
          size="lg"
          onClick={handleNext}
          iconName="ArrowRight"
          iconPosition="right"
          iconSize={18}
        >
          Continue to Portfolio
        </Button>
      </div>
    </div>
  );
};

export default ServicesForm;